import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, getStoredToken, setStoredToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If we just came back from Google OAuth, AuthCallback will handle it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    // UX polish sprint — same reasoning as signup: bcrypt verify + cold-start.
    const { data } = await api.post("/auth/login", { email, password }, { timeout: 60000 });
    setStoredToken(data.session_token);
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (email, password, name) => {
    // UX polish sprint — bcrypt hash on account creation can take 1-3s on cold
    // start; give this specific POST 60s so the button "Creating…" state doesn't
    // race the axios default (45s) on preview cold-starts.
    const { data } = await api.post("/auth/signup", { email, password, name }, { timeout: 60000 });
    setStoredToken(data.session_token);
    setUser(data.user);
    return data.user;
  }, []);

  const finishGoogle = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/google/session", {
      session_id: sessionId,
    });
    setStoredToken(data.session_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      // ignore
    }
    setStoredToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthed: !!user,
      hasToken: !!getStoredToken(),
      refresh,
      login,
      signup,
      logout,
      finishGoogle,
    }),
    [user, loading, refresh, login, signup, logout, finishGoogle]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
